import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { getStoredAccessToken } from './tokenStorage';

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);
const DEFAULT_DEV_API_PORT = (import.meta.env.VITE_API_PORT as string | undefined) ?? '5000';

function normalizeBase(url: string): string {
  return url.replace(/\/$/, '');
}

function buildFromLocation(portOverride?: string): string {
  if (typeof window === 'undefined') {
    return '/api';
  }

  const { protocol, hostname, port } = window.location;
  const portToUse = portOverride ?? port;
  const origin = `${protocol}//${hostname}${portToUse ? `:${portToUse}` : ''}`;
  return normalizeBase(`${origin}/api`);
}

export function resolveApiBaseUrl(): string {
  const configured = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();

  if (configured) {
    const normalized = normalizeBase(configured);

    if (typeof window !== 'undefined') {
      try {
        const configuredUrl = new URL(normalized, window.location.origin);
        const currentHost = window.location.hostname;

        if ((LOCAL_HOSTS.has(configuredUrl.hostname) || configuredUrl.hostname === 'web') && !LOCAL_HOSTS.has(currentHost)) {
          const inferredPort = configuredUrl.port || (configuredUrl.protocol === 'https:' ? '443' : '80');
          const portFragment = inferredPort ? `:${inferredPort}` : '';
          return normalizeBase(`${configuredUrl.protocol}//${currentHost}${portFragment}${configuredUrl.pathname}`);
        }

        return normalizeBase(configuredUrl.toString());
      } catch {
        return normalized;
      }
    }

    return normalized;
  }
  if (!import.meta.env.DEV && typeof window !== 'undefined' && window.location.port === '4173') {
    return buildFromLocation(DEFAULT_DEV_API_PORT);
  }

  return import.meta.env.DEV ? buildFromLocation(DEFAULT_DEV_API_PORT) : buildFromLocation();
}

export const API_BASE_URL = resolveApiBaseUrl();

const httpClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 60_000,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getStoredAccessToken();
  config.headers = config.headers ?? {};

  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (!config.headers['X-Api-Version']) {
    config.headers['X-Api-Version'] = '1.0';
  }

  return config;
});

httpClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response) {
      console.error('API error', error.response.status, error.response.config?.url, error.response.data);
    } else if (error.request) {
      console.error('API network error', error.message);
    } else {
      console.error('API configuration error', error.message);
    }

    return Promise.reject(error);
  }
);

export default httpClient;
